import { showUserAlert } from "../utils/userErrorMessage";
import React, { useEffect, useMemo, useState } from "react";
import Header from "../Component/Header";
import { supabase } from "../supabase/client";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import TimeSelect from "../components/ui/time-select";
import { getCarNumberMatchKey } from "../utils/dispatchConflictUtils";

const getToday = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const getMonthStart = () => {
  const today = getToday();
  return `${today.slice(0, 7)}-01`;
};

const getMonthEnd = () => {
  const today = new Date();
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
};

const makeEmptyForm = () => ({
  inspection_date: getToday(),
  inspection_time: "",
  car_number: "",
  driver_name: "",
  inspection_station: "",
  amount: "",
});

const toNumber = (value) => {
  const cleaned = String(value ?? "").replace(/[^0-9-]/g, "");
  if (!cleaned || cleaned === "-") return 0;
  return Number(cleaned) || 0;
};

const formatMoney = (value) => {
  const number = toNumber(value);
  return number ? number.toLocaleString() : "";
};

const normalizeTime = (value = "") => (value ? String(value).slice(0, 5) : "");

export default function BusInspectionReservations() {
  const [dateRange, setDateRange] = useState({ from: getMonthStart(), to: getMonthEnd() });
  const [rows, setRows] = useState([]);
  const [cars, setCars] = useState([]);
  const [form, setForm] = useState(() => makeEmptyForm());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const carByKey = useMemo(() => {
    const map = new Map();
    cars.forEach((car) => {
      [car.car_number, car.car_number_only].forEach((value) => {
        const key = getCarNumberMatchKey(value);
        if (key && !map.has(key)) map.set(key, car);
      });
    });
    return map;
  }, [cars]);

  const fillDriverFromCar = (carNumber, updater) => {
    const key = getCarNumberMatchKey(carNumber);
    const car = key ? carByKey.get(key) : null;
    updater((prev) => ({
      ...prev,
      car_number: carNumber,
      driver_name: car?.driver || prev.driver_name || "",
    }));
  };

  const loadCars = async () => {
    const company = localStorage.getItem("email") || "";
    if (!company) return;

    const { data, error } = await supabase
      .from("cars")
      .select("car_number, car_number_only, driver")
      .eq("company", company)
      .order("car_number_only", { ascending: true });

    if (error) {
      console.error("차량 목록 조회 실패:", error);
      return;
    }
    setCars(data || []);
  };

  const fetchRows = async () => {
    const company = localStorage.getItem("email") || "";
    if (!company) {
      showUserAlert("회사 정보가 없습니다. 다시 로그인해 주세요.");
      return;
    }

    setLoading(true);
    try {
      let query = supabase
        .from("bus_inspection_reservations")
        .select("*")
        .eq("company", company)
        .order("inspection_date", { ascending: true })
        .order("inspection_time", { ascending: true })
        .order("car_number", { ascending: true });

      if (dateRange.from) query = query.gte("inspection_date", dateRange.from);
      if (dateRange.to) query = query.lte("inspection_date", dateRange.to);

      const { data, error } = await query.range(0, 1999);
      if (error) throw error;
      setRows(data || []);
      setSelectedIds(new Set());
    } catch (error) {
      console.error("버스 검사 예약 조회 실패:", error);
      showUserAlert("버스 검사 예약 조회 실패: " + (error.message || JSON.stringify(error)));
      setRows([]);
      setSelectedIds(new Set());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCars();
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateForm = (field, value) => {
    if (field === "car_number") {
      fillDriverFromCar(value, setForm);
      return;
    }
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateRow = (index, field, value) => {
    setRows((prev) =>
      prev.map((row, rowIndex) => {
        if (rowIndex !== index) return row;
        if (field !== "car_number") return { ...row, [field]: value };
        const car = carByKey.get(getCarNumberMatchKey(value));
        return {
          ...row,
          car_number: value,
          driver_name: car?.driver || row.driver_name || "",
        };
      })
    );
  };

  const buildPayload = (source) => ({
    company: localStorage.getItem("email") || "",
    company_name: localStorage.getItem("companyName") || source.company_name || null,
    inspection_date: source.inspection_date || null,
    inspection_time: source.inspection_time || null,
    car_number: String(source.car_number || "").trim(),
    driver_name: String(source.driver_name || "").trim(),
    inspection_station: String(source.inspection_station || "").trim(),
    amount: toNumber(source.amount),
  });

  const validatePayload = (payload) => {
    if (!payload.inspection_date) return "일자를 입력해 주세요.";
    if (!payload.inspection_time) return "시간을 입력해 주세요.";
    if (!payload.car_number) return "차량번호를 입력해 주세요.";
    if (!payload.driver_name) return "기사명을 입력해 주세요.";
    if (!payload.inspection_station) return "검사소를 입력해 주세요.";
    return "";
  };

  const handleAdd = async () => {
    if (saving) return;
    const payload = buildPayload(form);
    const message = validatePayload(payload);
    if (message) return showUserAlert(message);

    setSaving(true);
    try {
      const { error } = await supabase.from("bus_inspection_reservations").insert(payload);
      if (error) throw error;
      setForm(makeEmptyForm());
      await fetchRows();
      showUserAlert("버스 검사 예약이 등록되었습니다.");
    } catch (error) {
      showUserAlert("버스 검사 예약 등록 실패: " + (error.message || JSON.stringify(error)));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (saving || deleting) return;
    const payloads = rows.map((row) => ({ id: row.id, ...buildPayload(row) }));
    const invalid = payloads.find((payload) => validatePayload(payload));
    if (invalid) return showUserAlert(validatePayload(invalid));

    setSaving(true);
    try {
      const { error } = await supabase
        .from("bus_inspection_reservations")
        .upsert(payloads, { onConflict: "id" });
      if (error) throw error;
      await fetchRows();
      showUserAlert("저장되었습니다.");
    } catch (error) {
      showUserAlert("저장 실패: " + (error.message || JSON.stringify(error)));
    } finally {
      setSaving(false);
    }
  };

  const toggleSelected = (id) => {
    if (!id) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allRowsSelected = rows.length > 0 && rows.every((row) => selectedIds.has(row.id));

  const toggleAllSelected = () => {
    setSelectedIds((prev) => {
      if (allRowsSelected) return new Set();
      const next = new Set(prev);
      rows.forEach((row) => {
        if (row.id) next.add(row.id);
      });
      return next;
    });
  };

  const handleDelete = async () => {
    if (saving || deleting) return;
    const ids = Array.from(selectedIds).filter(Boolean);
    if (ids.length === 0) {
      showUserAlert("삭제할 버스 검사 예약을 선택해 주세요.");
      return;
    }

    const ok = window.confirm(`선택한 버스 검사 예약 ${ids.length}건을 삭제하시겠습니까?`);
    if (!ok) return;

    setDeleting(true);
    try {
      const company = localStorage.getItem("email") || "";
      const { data, error } = await supabase
        .from("bus_inspection_reservations")
        .delete()
        .eq("company", company)
        .in("id", ids)
        .select("id");

      if (error) throw error;
      const deletedIds = new Set((data || []).map((row) => row.id));
      if (deletedIds.size === 0) {
        throw new Error("DB에서 삭제된 행이 없습니다. RLS 삭제 정책 또는 삭제 조건을 확인해 주세요.");
      }

      setRows((prev) => prev.filter((row) => !deletedIds.has(row.id)));
      setSelectedIds(new Set());
      showUserAlert("삭제되었습니다.");
    } catch (error) {
      console.error("버스 검사 예약 삭제 실패:", error);
      showUserAlert("삭제 실패: " + (error.message || JSON.stringify(error)));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-1">
      <Header />

      <div className="px-4 pt-1 pb-4 space-y-3">
        <h2 className="text-lg font-semibold">버스 검사 예약 관리</h2>

        <Card className="border-0 shadow-none">
          <CardContent className="bg-white p-4">
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <label className="mb-1 block text-sm font-semibold">조회 일자</label>
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={dateRange.from}
                    onChange={(event) => setDateRange((prev) => ({ ...prev, from: event.target.value }))}
                  />
                  <span className="text-sm">~</span>
                  <Input
                    type="date"
                    value={dateRange.to}
                    onChange={(event) => setDateRange((prev) => ({ ...prev, to: event.target.value }))}
                  />
                </div>
              </div>
              <Button onClick={fetchRows} disabled={loading || saving}>
                {loading ? "조회중..." : "검색"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="bg-white">
            <div className="overflow-auto">
              <table className="w-full table-auto border text-sm whitespace-nowrap">
                <thead className="sticky top-0 z-20 bg-blue-500 text-white">
                  <tr>
                    <th className="border px-2 py-1 text-center">
                      <input
                        type="checkbox"
                        checked={allRowsSelected}
                        onChange={toggleAllSelected}
                        disabled={rows.length === 0 || saving || deleting}
                      />
                    </th>
                    {["순번", "일자", "시간", "차량번호", "기사명", "검사소", "금액"].map((label) => (
                      <th key={label} className="border px-2 py-1 text-center">
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-orange-50">
                    <td className="border px-2 py-1 text-center"></td>
                    <td className="border px-2 py-1 text-center font-semibold">추가</td>
                    <td className="border px-2 py-1">
                      <Input
                        type="date"
                        value={form.inspection_date}
                        onChange={(event) => updateForm("inspection_date", event.target.value)}
                        className="min-w-[140px] px-2 py-0.5"
                      />
                    </td>
                    <td className="border px-2 py-1">
                      <TimeSelect
                        value={form.inspection_time}
                        onChange={(value) => updateForm("inspection_time", value)}
                        compact
                        showClear={false}
                      />
                    </td>
                    <td className="border px-2 py-1">
                      <Input
                        list="bus-inspection-cars"
                        value={form.car_number}
                        onChange={(event) => updateForm("car_number", event.target.value)}
                        className="min-w-[110px] px-2 py-0.5"
                      />
                    </td>
                    <td className="border px-2 py-1">
                      <Input
                        value={form.driver_name}
                        onChange={(event) => updateForm("driver_name", event.target.value)}
                        className="min-w-[100px] px-2 py-0.5"
                      />
                    </td>
                    <td className="border px-2 py-1">
                      <Input
                        value={form.inspection_station}
                        onChange={(event) => updateForm("inspection_station", event.target.value)}
                        className="min-w-[180px] px-2 py-0.5"
                      />
                    </td>
                    <td className="border px-2 py-1 text-right">
                      <Input
                        value={formatMoney(form.amount)}
                        onChange={(event) => updateForm("amount", toNumber(event.target.value))}
                        className="min-w-[110px] px-2 py-0.5 text-right"
                      />
                    </td>
                  </tr>

                  {rows.length === 0 ? (
                    <tr>
                      <td className="border px-2 py-8 text-center text-gray-500" colSpan={8}>
                        {loading ? "조회 중입니다." : "등록된 버스 검사 예약이 없습니다."}
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, index) => (
                      <tr key={row.id || index} className="hover:bg-gray-50">
                        <td className="border px-2 py-1 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(row.id)}
                            onChange={() => toggleSelected(row.id)}
                            disabled={saving || deleting}
                          />
                        </td>
                        <td className="border px-2 py-1 text-center">{index + 1}</td>
                        <td className="border px-2 py-1">
                          <Input
                            type="date"
                            value={row.inspection_date || ""}
                            onChange={(event) => updateRow(index, "inspection_date", event.target.value)}
                            className="min-w-[140px] px-2 py-0.5"
                          />
                        </td>
                        <td className="border px-2 py-1">
                          <TimeSelect
                            value={normalizeTime(row.inspection_time)}
                            onChange={(value) => updateRow(index, "inspection_time", value)}
                            compact
                            showClear={false}
                          />
                        </td>
                        <td className="border px-2 py-1">
                          <Input
                            list="bus-inspection-cars"
                            value={row.car_number || ""}
                            onChange={(event) => updateRow(index, "car_number", event.target.value)}
                            className="min-w-[110px] px-2 py-0.5"
                          />
                        </td>
                        <td className="border px-2 py-1">
                          <Input
                            value={row.driver_name || ""}
                            onChange={(event) => updateRow(index, "driver_name", event.target.value)}
                            className="min-w-[100px] px-2 py-0.5"
                          />
                        </td>
                        <td className="border px-2 py-1">
                          <Input
                            value={row.inspection_station || ""}
                            onChange={(event) => updateRow(index, "inspection_station", event.target.value)}
                            className="min-w-[180px] px-2 py-0.5"
                          />
                        </td>
                        <td className="border px-2 py-1">
                          <Input
                            value={formatMoney(row.amount)}
                            onChange={(event) => updateRow(index, "amount", toNumber(event.target.value))}
                            className="min-w-[110px] px-2 py-0.5 text-right"
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <datalist id="bus-inspection-cars">
              {cars.map((car) => (
                <option
                  key={`${car.car_number || ""}-${car.car_number_only || ""}`}
                  value={car.car_number_only || car.car_number || ""}
                >
                  {[car.car_number, car.driver].filter(Boolean).join(" / ")}
                </option>
              ))}
            </datalist>

            <div className="mt-3 flex justify-end gap-2">
              <Button
                type="button"
                onClick={handleDelete}
                disabled={saving || loading || deleting || selectedIds.size === 0}
                className="bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-300"
              >
                {deleting ? "삭제중..." : "삭제"}
              </Button>
              <Button variant="outline" onClick={handleAdd} disabled={saving || loading || deleting}>
                추가등록
              </Button>
              <Button onClick={handleSave} disabled={saving || loading || deleting || rows.length === 0}>
                {saving ? "저장중..." : "저장"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
